import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Loader2, CheckCircle, XCircle, Clock, AlertTriangle, Briefcase, ChevronRight, RefreshCw, Ban, X, FileSpreadsheet, Upload, Brain, Sparkles, Link2, TrendingUp, History, Target, Search } from "lucide-react";
import OfferWizard from "@/components/OfferWizard";
import ImportOfferJsonModal from "@/components/offer/ImportOfferJsonModal";
import LinkLicitacionModal from "@/components/offer/LinkLicitacionModal";
import LinkTender360Modal from "@/components/offer/LinkTender360Modal";
import DGFilter, { useDGFilter, DG_LABELS } from "@/components/DGFilter";
import { CONTRACT_TYPES, contractTypeLabel } from "@/lib/offerConstants";

const fmt = (n) => n > 0 ? `${Number(n).toLocaleString("es-ES", {minimumFractionDigits: 2, maximumFractionDigits: 2})}€` : "—";
// Si el OPEX viene desglosado por anualidad completa ("Anualidad 1", "Anualidad
// 2"...), opex_total ya es el OPEX de TODO el contrato, no de 1 año — la columna
// "OPEX/año" debe mostrar el promedio, no la suma total.
const opexAnnual = (o) => {
  const opexSecs = (o.cost_sections || []).filter(s => s.category === "OPEX");
  const isMultiYear = opexSecs.length >= 2 &&
    opexSecs.every(s => /^anualidad\s*\d+$/i.test((s.section_name || "").trim()));
  return isMultiYear ? (o.opex_total || 0) / opexSecs.length : (o.opex_total || 0);
};
const fmtCompactEur = (n) => {
  if (!n || n <= 0) return "—";
  if (n >= 1_000_000) return `${(n/1_000_000).toFixed(1)}M€`;
  if (n >= 1_000) return `${Math.round(n/1_000)}k€`;
  return `${Math.round(n)}€`;
};

// Iter222 · Semáforo de vinculación basado en linked_licitacion_at + estado
function LinkHealthBadge({ offer }) {
  if (!offer?.linked_licitacion_id) return null;
  const at = offer.linked_licitacion_at;
  let ageDays = null;
  if (at) {
    try { ageDays = (Date.now() - new Date(at).getTime()) / 86400000; } catch { /* noop */ }
  }
  const state = (offer.linked_licitacion_state || "").toUpperCase();
  // Rojo si es RESUELTA/DESIERTA/CANCELADA, si no hay linked_at (legacy) o si >30 días
  // Ámbar si linked_at entre 7 y 30 días
  // Verde si linked_at < 7 días
  const badState = /(RESUELTA|DESIERTA|CANCELADA|RECHAZADA|ORPHANED)/.test(state);
  let level, tip;
  if (badState) { level = "red"; tip = "La licitación asociada está resuelta o cancelada."; }
  else if (ageDays === null) { level = "red"; tip = "Vinculación legacy sin fecha — ejecuta el matcher para refrescar."; }
  else if (ageDays > 30) { level = "red"; tip = `Vinculación con ${Math.round(ageDays)} días sin refresh.`; }
  else if (ageDays > 7) { level = "amber"; tip = `Vínculo de ${Math.round(ageDays)} días — revisar estado.`; }
  else { level = "green"; tip = "Vinculación reciente y saludable."; }
  const cls = level === "green" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
            : level === "amber" ? "bg-amber-100 text-amber-800 border-amber-200"
            :                     "bg-rose-100 text-rose-800 border-rose-200";
  const dot = level === "green" ? "🟢" : level === "amber" ? "🟡" : "🔴";
  return (
    <span title={tip} data-testid={`offer-link-health-${offer.id}`}
      className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-semibold border ${cls}`}>
      {dot} {state || "vinculada"}
    </span>
  );
}

// Iter222 · Donut minimalista SVG para el reparto de estados
function StatusDonut({ segments, size = 128 }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  if (!total) return <div className="text-xs text-gray-400 text-center py-6">Sin datos suficientes</div>;
  const stroke = 18;
  const radius = (size - stroke) / 2;
  const cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="status-donut">
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#f3f4f6" strokeWidth={stroke} />
      {segments.map((seg, i) => {
        const frac = (seg.value || 0) / total;
        const dash = frac * circumference;
        const gap = circumference - dash;
        const rot = -90 + (acc / total) * 360;
        acc += seg.value || 0;
        return (
          <circle key={i} cx={cx} cy={cy} r={radius} fill="none" stroke={seg.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${gap}`} transform={`rotate(${rot} ${cx} ${cy})`} strokeLinecap="butt" />
        );
      })}
      <text x={cx} y={cy - 3} textAnchor="middle" className="fill-gray-900" style={{ fontSize: 18, fontWeight: 700, fontFamily: "Outfit, sans-serif" }}>
        {total}
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" className="fill-gray-400" style={{ fontSize: 9 }}>
        ofertas
      </text>
    </svg>
  );
}

/**
 * Badge de salud financiera para ofertas TIC con business case.
 *   🟢 IRR ≥ 25 %  · saludable
 *   🟡 IRR 15-25 % · moderada
 *   🔴 IRR < 15 %  · en riesgo
 * Si no hay IRR pero hay margen EBITDA, cae al criterio: ≥ 20 % 🟢 · 10-20 % 🟡 · < 10 % 🔴.
 */
function FinancialHealthBadge({ bc, aiImported, offerId }) {
  if (!bc || typeof bc !== "object") return null;
  const irr = typeof bc.unlevered_irr === "number" ? bc.unlevered_irr : null;
  const margin = typeof bc.ebitda_margin_pct === "number" ? bc.ebitda_margin_pct : null;
  let level = null; let value = null; let label = null;
  // Iter304 · D08/E07 — un IRR fuera de [-100%, 100%] no es una señal
  // financiera creíble para este negocio (contratos de servicios), suele
  // indicar un error de escala/cálculo. No se "arregla" el número ni se
  // inventa un límite de negocio: se marca para revisión en vez de
  // mostrarlo en verde como si fuera saludable.
  if (irr != null && Math.abs(irr) > 1) {
    value = `IRR ${(irr * 100).toFixed(1)}%`;
    level = "flag"; label = "Revisar dato (fuera de rango esperado)";
  } else if (irr != null) {
    value = `IRR ${(irr * 100).toFixed(1)}%`;
    if (irr >= 0.25) { level = "green"; label = "Saludable"; }
    else if (irr >= 0.15) { level = "amber"; label = "Moderada"; }
    else { level = "red"; label = "En riesgo"; }
  } else if (margin != null) {
    value = `EBITDA ${(margin * 100).toFixed(1)}%`;
    if (margin >= 0.20) { level = "green"; label = "Saludable"; }
    else if (margin >= 0.10) { level = "amber"; label = "Moderada"; }
    else { level = "red"; label = "En riesgo"; }
  } else {
    return null;
  }
  const cls = level === "green"
    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : level === "amber"
    ? "bg-amber-100 text-amber-800 border-amber-200"
    : level === "flag"
    ? "bg-purple-100 text-purple-800 border-purple-300"
    : "bg-rose-100 text-rose-800 border-rose-200";
  const dot = level === "green" ? "🟢" : level === "amber" ? "🟡" : level === "flag" ? "🔎" : "🔴";
  return (
    <div className="flex items-center gap-1 mt-1">
      <span
        data-testid={`offer-financial-health-${offerId}`}
        title={`Salud financiera: ${label}`}
        className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold border ${cls}`}
      >
        <span>{dot}</span> {label} · {value}
      </span>
      {aiImported && (
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold border border-indigo-200" title="Importada vía Wizard Pando">
          🤖 Pando
        </span>
      )}
    </div>
  );
}

export default function Offers() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isOfferManager = user?.role === "offer_manager";

  const STATUS_MAP = {
    draft:                  { label: t("offers.states.draft"),              cls: "bg-gray-100 text-gray-600",  icon: Clock },
    borrador:               { label: t("offers.states.borrador"),           cls: "bg-gray-100 text-gray-600",  icon: Clock },
    pending_validation:     { label: t("offers.states.pending_validation"), cls: "bg-amber-100 text-amber-700", icon: AlertTriangle },
    pending_it_validation:  { label: t("offers.states.pending_validation"), cls: "bg-amber-100 text-amber-700", icon: AlertTriangle },
    pending_cfo_approval:   { label: t("offers.states.pending_cfo"),        cls: "bg-blue-100 text-blue-700",   icon: AlertTriangle },
    approved:               { label: t("offers.states.approved"),           cls: "bg-green-100 text-green-700", icon: CheckCircle },
    approved_with_conditions: { label: t("offers.states.approved_with_conditions"), cls: "bg-amber-100 text-amber-800", icon: AlertTriangle },
    rejected:               { label: t("offers.states.rejected"),           cls: "bg-red-100 text-red-700",     icon: XCircle },
    desestimada:            { label: t("offers.states.desestimada"),        cls: "bg-slate-100 text-slate-500 line-through", icon: Ban },
  };

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportJson, setShowImportJson] = useState(false);
  const [creating, setCreating] = useState(false);
  const [desestimando, setDesestimando] = useState(null);
  // AI-powered wizard
  const [showWizard, setShowWizard] = useState(false);
  // iter134 · Filtro DG global (Residuos/Servicios Urbanos)
  const { dg, setDg, filterByDG } = useDGFilter(user?.email);
  // Iter313 · G03 — filtros persistentes (búsqueda + estado) reflejados en la
  // URL, mismo patrón que OfertasComercial/Mercado.
  const [q, setQ] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("q") || ""; } catch { return ""; }
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("estado") || ""; } catch { return ""; }
  });
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const setOrDel = (key, val) => { if (val) url.searchParams.set(key, val); else url.searchParams.delete(key); };
      setOrDel("q", q);
      setOrDel("estado", statusFilter);
      window.history.replaceState({}, "", url.toString());
    } catch { /* no-op */ }
  }, [q, statusFilter]);
  const offersF = filterByDG(offers).filter(o => {
    if (statusFilter && o.status !== statusFilter) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      const haystack = `${o.title || ""} ${o.licitacion || ""} ${o.municipality || ""} ${o.responsible_business || ""}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    return true;
  });

  const [form, setForm] = useState({ title: "", licitacion: "", contract_type: "SER", contract_type_other: "", municipality: "", deadline: "", responsible_business: "", responsible_business_email: "", description: "" });
  const navigate = useNavigate();

  // Iter221 · KPIs agregados (by_status + by_type + by_linked)
  const [kpis, setKpis] = useState(null);
  const loadKpis = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/offers/kpis`);
      setKpis(data);
    } catch (e) { console.warn("KPIs no disponibles", e); }
  }, []);
  useEffect(() => { loadKpis(); }, [loadKpis]);

  // Iter222 · Historial de matches + modal vinculación manual
  const [matchHistory, setMatchHistory] = useState(null);
  const loadMatchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/offers/match-history`, { params: { limit: 5 } });
      setMatchHistory(data);
    } catch (e) { console.warn("match-history no disponible", e); }
  }, []);
  useEffect(() => { loadMatchHistory(); }, [loadMatchHistory]);
  const [linkingOffer, setLinkingOffer] = useState(null);
  const [linkingTender360Offer, setLinkingTender360Offer] = useState(null);

  // Iter221 · Trigger manual del matcher offers ↔ licitaciones
  const [matching, setMatching] = useState(false);
  const runMatcher = async () => {
    if (matching) return;
    if (!window.confirm("⚠ Vas a ejecutar el MATCHER de Licitaciones ahora (actualiza el estado de todas las ofertas TIC). ¿Continuar?")) return;
    setMatching(true);
    try {
      const { data } = await axios.post(`${API}/offers/admin/match-licitaciones`);
      await Promise.all([load(), loadKpis(), loadMatchHistory()]);
      alert(`Matcher ejecutado · ${data.matched_new || 0} nuevos, ${data.matched_updated || 0} actualizados, ${data.no_match || 0} sin match (${data.checked || 0} revisadas).`);
    } catch (e) {
      alert(e?.response?.data?.detail || "No se pudo ejecutar el matcher");
    } finally { setMatching(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/offers`);
      setOffers(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/offers`, { ...form, cost_sections: [] });
      setShowModal(false);
      navigate(`/offers/${data.id}`);
    } catch (e) { alert("Error creando la oferta"); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar esta oferta?")) return;
    await axios.delete(`${API}/offers/${id}`);
    load(); loadKpis();
  };

  const handleDesestimar = async () => {
    if (!desestimando) return;
    try {
      await axios.post(`${API}/offers/${desestimando.id}/desestimar`, { motivo: desestimando.motivo || "Licitación cancelada" });
      setDesestimando(null);
      load(); loadKpis();
    } catch (e) { alert("Error al desestimar"); }
  };

  const now = new Date();
  const daysUntil = (deadline) => {
    if (!deadline) return null;
    const diff = Math.ceil((new Date(deadline) - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="space-y-5 w-full" data-testid="offers-page">
      {/* Desestimar Modal */}
      {desestimando && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-gray-900 flex items-center gap-2"><Ban size={16} className="text-slate-500" /> Desestimar oferta</h2>
              <button onClick={() => setDesestimando(null)}><X size={18} className="text-gray-400" /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">La oferta pasará a estado <strong>Desestimada</strong> porque la licitación fue cancelada o no prosperó.</p>
            <div className="mb-4">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Motivo (opcional)</label>
              <textarea rows={3} value={desestimando.motivo} onChange={e => setDesestimando(d => ({ ...d, motivo: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
                placeholder="Ej: Licitación cancelada por el Ayuntamiento, cambios en presupuesto municipal..." />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDesestimando(null)} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
              <button onClick={handleDesestimar} className="flex-1 bg-slate-600 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-slate-700 flex items-center justify-center gap-2" data-testid="confirm-desestimar-btn">
                <Ban size={14} /> Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">
            {t("offers.title")}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {t("offers.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 text-gray-400 hover:text-gray-600 bg-white rounded-lg border border-gray-100"><RefreshCw size={15} /></button>
          <button
            onClick={runMatcher}
            disabled={matching}
            className="flex items-center gap-2 border border-blue-200 text-blue-700 px-3 py-2.5 rounded-xl text-sm font-medium hover:bg-blue-50 transition-all disabled:opacity-60"
            data-testid="run-licit-matcher-btn"
            title="Sincroniza ahora el estado de cada oferta TIC con su Licitación correspondiente (cron diario a las 06:15 UTC)."
          >
            {matching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Match Licitaciones
          </button>
          <button
            onClick={() => setShowImportJson(true)}
            className="flex items-center gap-2 border border-gray-200 text-gray-700 px-3 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition-all"
            data-testid="import-offer-json-btn"
            title="Importar una oferta desde un JSON exportado (p.ej. desde Preview a Producción)"
          >
            <Upload size={15} /> Importar JSON
          </button>
          <button
            onClick={() => setShowWizard(true)}
            className="flex items-center gap-2 border border-[#007934] text-[#007934] px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#E8F5E9] transition-all"
            data-testid="import-excel-btn"
          >
            <Sparkles size={15} /> Wizard Pando — desde Excel
          </button>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#006528] shadow-sm" data-testid="new-offer-btn">
            <Plus size={15} /> Nueva oferta
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <DGFilter value={dg} onChange={setDg} size="sm" testidPrefix="offers-dg-filter"/>
        {dg && (
          <span className="text-[11px] text-slate-500" data-testid="offers-dg-active-note">
            Filtro <b className="text-slate-700">{DG_LABELS[dg]}</b> · {offersF.length} de {offers.length} ofertas
          </span>
        )}
      </div>

      {/* C05 (CRM audit 2026-10-13) · Las ofertas TIC (borradores IT · CAPEX/OPEX)
          NO se clasifican por Dirección General. Antes, un filtro SU/DG heredado
          dejaba la lista en "0 de 23" pero seguía mostrando KPIs globales (23/17/6,
          CAPEX 11,2 M€) sobre una lista vacía. Ahora se explica la cobertura y se
          etiquetan los KPIs como "del universo total". */}
      {dg && dg !== "SIN_DG" && offersF.length === 0 && offers.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2.5" data-testid="offers-dg-coverage-note">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-900">
            Las ofertas TIC no se clasifican por Dirección General, así que el filtro
            «<b>{DG_LABELS[dg]}</b>» deja la lista vacía (<b>0 de {offers.length}</b>).
            Los indicadores de abajo se calculan sobre las <b>{offers.length} ofertas totales</b>, no sobre la vista filtrada.{" "}
            <button type="button" onClick={() => setDg("")} className="font-semibold underline hover:text-amber-700" data-testid="offers-dg-coverage-clear">
              Quitar filtro DG
            </button>
          </p>
        </div>
      )}

      {/* Iter221 · KPI Panels (backend /api/offers/kpis) */}
      {kpis && (
        <>
        {dg && dg !== "SIN_DG" && (
          <p className="text-[11px] text-slate-400 -mb-1" data-testid="offers-kpis-universe-note">
            Indicadores sobre las {offers.length} ofertas totales (las ofertas TIC no se filtran por DG).
          </p>
        )}
        {/* Iter222 · Win ratio + Donut de estados + Health */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="offers-visual-panels">
          <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4" data-testid="offers-winratio-card">
            <StatusDonut size={112} segments={[
              { label: "Aprobadas", value: (kpis.by_status || []).find(s => s.status === "approved")?.count || 0, color: "#059669" },
              { label: "Pdte CFO",  value: (kpis.by_status || []).find(s => s.status === "pending_cfo_approval")?.count || 0, color: "#2563eb" },
              { label: "Pdte TIC",  value: ((kpis.by_status || []).find(s => s.status === "pending_it_validation")?.count || 0) + ((kpis.by_status || []).find(s => s.status === "pending_validation")?.count || 0), color: "#f59e0b" },
              { label: "Rechazadas", value: (kpis.by_status || []).find(s => s.status === "rejected")?.count || 0, color: "#dc2626" },
              { label: "Desestimadas", value: (kpis.by_status || []).find(s => s.status === "desestimada")?.count || 0, color: "#64748b" },
              { label: "Borrador", value: ((kpis.by_status || []).find(s => s.status === "draft")?.count || 0) + ((kpis.by_status || []).find(s => s.status === "borrador")?.count || 0), color: "#d1d5db" },
            ]}/>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1"><TrendingUp size={12}/> Tasa aprobación interna TIC</p>
              {typeof kpis.win_ratio === "number" ? (
                <>
                  <p className="text-3xl font-bold text-emerald-700 font-['Outfit']" data-testid="offers-winratio-value">
                    {(kpis.win_ratio * 100).toFixed(1)}<span className="text-lg">%</span>
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    <b>{kpis.won}</b> aprobadas · <b>{kpis.lost}</b> rechazadas · <span className="text-gray-400">{kpis.closed} cerradas</span>
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-400 mt-2">Aún sin cierres</p>
              )}
              <p className="text-[10px] text-gray-400 mt-2">% de validación interna IT/CFO, no es tasa de éxito frente a competidores · Aprobadas / (Aprobadas + Rechazadas + Desestimadas)</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="offers-status-breakdown">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Reparto por estado (magnitud)</p>
            <div className="space-y-1.5 text-xs">
              {[
                ["approved", "Aprobadas", "text-emerald-700"],
                ["pending_cfo_approval", "Pendiente CFO", "text-blue-700"],
                ["pending_it_validation", "Pendiente TIC", "text-amber-700"],
                ["rejected", "Rechazadas", "text-rose-700"],
                ["desestimada", "Desestimadas", "text-slate-500"],
                ["draft", "Borrador", "text-gray-500"],
              ].map(([key, label, cls]) => {
                const amt = (kpis.amount_by_status || {})[key] || 0;
                const cnt = (kpis.by_status || []).find(x => x.status === key)?.count || 0;
                if (!cnt) return null;
                return (
                  <div key={key} className="flex items-center justify-between border-b border-gray-50 py-1" data-testid={`offers-status-row-${key}`}>
                    <span className={`font-semibold ${cls}`}>{label}</span>
                    <span className="text-gray-600">{cnt} · <b className="text-gray-800">{fmtCompactEur(amt)}</b></span>
                  </div>
                );
              })}
            </div>
            {/* C05 · denominación clara de Rechazada vs Desestimada */}
            <p className="text-[10px] text-gray-400 mt-2 pt-2 border-t border-gray-50" data-testid="offers-rechazada-desestimada-note">
              <b className="text-rose-600">Rechazada</b>: validación interna IT/CFO no aprobada · <b className="text-slate-500">Desestimada</b>: la licitación asociada se canceló o no prosperó. Ambas cuentan como «no aprobada» en la tasa interna.
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="offers-linkhealth-card">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">Salud del vínculo con Licitaciones</p>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2" data-testid="offers-linkhealth-fresh">
                <p className="text-[10px] text-emerald-700 uppercase">🟢 Reciente</p>
                <p className="text-xl font-bold text-emerald-800 font-['Outfit']">{kpis.linked_health?.fresh || 0}</p>
                <p className="text-[9px] text-emerald-600">&lt; 7 días</p>
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2" data-testid="offers-linkhealth-warn">
                <p className="text-[10px] text-amber-700 uppercase">🟡 A revisar</p>
                <p className="text-xl font-bold text-amber-800 font-['Outfit']">{kpis.linked_health?.warn || 0}</p>
                <p className="text-[9px] text-amber-600">7-30 días</p>
              </div>
              <div className="rounded-lg bg-rose-50 border border-rose-100 px-3 py-2" data-testid="offers-linkhealth-stale">
                <p className="text-[10px] text-rose-700 uppercase">🔴 Obsoleta</p>
                <p className="text-xl font-bold text-rose-800 font-['Outfit']">{kpis.linked_health?.stale || 0}</p>
                <p className="text-[9px] text-rose-600">&gt; 30 días</p>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2" data-testid="offers-linkhealth-unlinked">
                <p className="text-[10px] text-slate-500 uppercase">⚪ Sin vínculo</p>
                <p className="text-xl font-bold text-slate-700 font-['Outfit']">{kpis.linked_health?.unlinked || 0}</p>
                <p className="text-[9px] text-slate-400">no vinculadas</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="offers-kpi-panels">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Ofertas TIC por tipo de contrato</p>
              <span className="text-[10px] text-gray-400">Total {kpis.total}</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {CONTRACT_TYPES.map(ct => {
                const found = (kpis.by_type || []).find(x => (x.contract_type || "").toUpperCase() === ct.code);
                const count = found?.count || 0;
                return (
                  <div key={ct.code} className="rounded-lg border border-gray-100 px-3 py-2 hover:bg-gray-50" data-testid={`offers-kpi-type-${ct.code}`}>
                    <p className="text-[10px] text-gray-400 truncate" title={ct.label}>{ct.label}</p>
                    <p className="text-lg font-bold text-gray-900 font-['Outfit']">{count}</p>
                    <p className="text-[10px] text-gray-400 uppercase">{ct.code}</p>
                  </div>
                );
              })}
            </div>
            {(() => {
              const catalogCodes = CONTRACT_TYPES.map(ct => ct.code);
              const legacy = (kpis.by_type || []).filter(x => !catalogCodes.includes((x.contract_type || "").toUpperCase()));
              if (!legacy.length) return null;
              return (
                <div className="mt-3 pt-3 border-t border-gray-50">
                  <p className="text-[10px] text-amber-700 uppercase mb-2 font-semibold">⚠ Sin catalogar / legacy</p>
                  <div className="flex flex-wrap gap-2">
                    {legacy.map(l => (
                      <span key={l.contract_type} className="inline-flex items-center gap-1 text-[10px] bg-amber-50 border border-amber-100 text-amber-800 px-2 py-1 rounded" data-testid={`offers-kpi-legacy-${l.contract_type}`}>
                        <b>{l.contract_type}</b>·{l.count}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Sincronización con Licitaciones</p>
              <span className="text-[10px] text-gray-400">Cron 06:15 UTC</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2" data-testid="offers-kpi-linked">
                <p className="text-[10px] text-blue-600 uppercase">Vinculadas</p>
                <p className="text-2xl font-bold text-blue-800 font-['Outfit']">{kpis.matched}</p>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2" data-testid="offers-kpi-unlinked">
                <p className="text-[10px] text-slate-500 uppercase">Sin licitación</p>
                <p className="text-2xl font-bold text-slate-700 font-['Outfit']">{kpis.unmatched}</p>
              </div>
            </div>
            {(kpis.by_linked || []).length > 0 && (
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {kpis.by_linked.slice(0, 5).map(row => (
                  <div key={row.linked_state} className="flex items-center justify-between text-[11px] text-gray-600 border-b border-gray-50 py-1">
                    <span className="truncate max-w-[220px]" title={row.linked_state}>{row.linked_state}</span>
                    <span className="font-semibold text-gray-800">{row.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </>
      )}

      {/* Iter222 · Historial del matcher (cron 06:15 UTC + manual) */}
      {matchHistory && matchHistory.items && matchHistory.items.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4" data-testid="match-history-panel">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
              <History size={12}/> Historial de ejecuciones del matcher
            </p>
            <span className="text-[10px] text-gray-400">Últimas {matchHistory.items.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 uppercase text-[9px]">
                  <th className="text-left py-1">Fecha</th>
                  <th className="text-left py-1">Trigger</th>
                  <th className="text-right py-1">Revisadas</th>
                  <th className="text-right py-1">Nuevos</th>
                  <th className="text-right py-1">Actualizados</th>
                  <th className="text-right py-1">Sin match</th>
                  <th className="text-right py-1">Duración</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {matchHistory.items.map(row => (
                  <tr key={row.id} data-testid={`match-history-row-${row.id}`}>
                    <td className="py-1.5 text-gray-700">{(row.started_at || "").replace("T", " ").slice(0, 16)}</td>
                    <td className="py-1.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase ${row.trigger === "manual" ? "bg-blue-50 text-blue-700" : "bg-slate-50 text-slate-600"}`}>
                        {row.trigger || "auto"}{row.run_by ? ` · ${row.run_by.split("@")[0]}` : ""}
                      </span>
                    </td>
                    <td className="py-1.5 text-right text-gray-600">{row.checked || 0}</td>
                    <td className="py-1.5 text-right font-semibold text-emerald-700">{row.matched_new || 0}</td>
                    <td className="py-1.5 text-right text-blue-700">{row.matched_updated || 0}</td>
                    <td className="py-1.5 text-right text-rose-600">{row.no_match || 0}</td>
                    <td className="py-1.5 text-right text-gray-400">{row.duration_ms || 0} ms</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Iter313 · G03 — filtros persistentes (búsqueda + estado) */}
      <div className="flex flex-wrap items-center gap-2" data-testid="offers-filters-bar">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar: título, licitación, municipio, responsable…"
            className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/30"
            data-testid="offers-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#007934]/30"
          data-testid="offers-status-filter"
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_MAP).map(([key, v]) => (
            <option key={key} value={key}>{v.label}</option>
          ))}
        </select>
        {(q || statusFilter) && (
          <button onClick={() => { setQ(""); setStatusFilter(""); }}
            className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
            data-testid="offers-filters-clear">
            <X size={12} /> Limpiar filtros
          </button>
        )}
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "Total", val: offersF.length, cls: "text-gray-900" },
          { label: "Pendiente TIC", val: offersF.filter(o => ["pending_validation","pending_it_validation"].includes(o.status)).length, cls: "text-amber-700" },
          { label: "Pendiente CFO", val: offersF.filter(o => o.status === "pending_cfo_approval").length, cls: "text-blue-700" },
          { label: "Aprobadas", val: offersF.filter(o => o.status === "approved").length, cls: "text-green-700" },
          { label: "CAPEX total", val: fmt(offersF.reduce((s, o) => s + (o.capex_total || 0), 0)), cls: "text-[#007934]" },
        ].map(({ label, val, cls }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 text-center">
            <p className={`text-xl font-bold font-['Outfit'] ${cls}`}>{val}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Offers table */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><Loader2 size={28} className="animate-spin text-[#007934]" /></div>
      ) : offers.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <Briefcase size={40} className="mx-auto mb-3 text-gray-200" />
          <p className="text-gray-400 text-sm">No hay ofertas. Crea la primera.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 uppercase border-b border-gray-50">
                <th className="text-left px-5 py-3">Oferta / Licitación</th>
                <th className="text-left px-4 py-3">Tipo</th>
                <th className="text-left px-4 py-3">Municipio</th>
                <th className="text-left px-4 py-3">Responsable</th>
                <th className="text-right px-4 py-3">CAPEX</th>
                <th className="text-right px-4 py-3">OPEX/año</th>
                <th className="text-left px-4 py-3">Deadline</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {offersF.map(o => {
                const st = STATUS_MAP[o.status] || STATUS_MAP.draft;
                const Icon = st.icon;
                const days = daysUntil(o.deadline);
                return (
                  <tr key={o.id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/offers/${o.id}`)} data-testid={`offer-row-${o.id}`}>
                    <td className="px-5 py-4">
                      <p className="font-semibold text-gray-900 text-sm">{o.title}</p>
                      {o.licitacion && <p className="text-xs text-gray-400 mt-0.5">{o.licitacion}</p>}
                      <FinancialHealthBadge bc={o.business_case} aiImported={o.imported_by_ai} offerId={o.id} />
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-xs bg-[#E8F5E9] text-[#007934] px-2 py-1 rounded-full font-semibold" title={contractTypeLabel(o.contract_type, o.contract_type_other)}>{o.contract_type}</span>
                      {o.linked_licitacion_id ? (
                        <div className="mt-1">
                          <LinkHealthBadge offer={o} />
                          {o.linked_manual && <span className="ml-1 text-[8px] text-blue-500 uppercase" title="Vinculación manual">manual</span>}
                        </div>
                      ) : (
                        <div className="text-[9px] text-slate-400 mt-1 italic" title="Sin licitación vinculada">Sin licitación</div>
                      )}
                      {o.linked_tender360_id ? (
                        <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded" title={o.linked_tender360_state} data-testid={`offer-tender360-${o.id}`}>
                          <Target size={9} /> Tender360
                        </div>
                      ) : (
                        <div className="mt-1 text-[9px] text-amber-500 italic" title="Sin vínculo con Tender360" data-testid={`offer-no-tender360-${o.id}`}>Sin Tender360</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-xs text-gray-600">{o.municipality || "—"}</td>
                    <td className="px-4 py-4 text-xs text-gray-600">{o.responsible_business || "—"}</td>
                    <td className="px-4 py-4 text-right font-semibold text-[#007934] text-sm">{fmt(o.capex_total)}</td>
                    <td className="px-4 py-4 text-right text-gray-600 text-sm">{fmt(opexAnnual(o))}</td>
                    <td className="px-4 py-4">
                      {o.deadline ? (
                        <div>
                          <p className="text-xs font-medium">{o.deadline}</p>
                          {days !== null && (
                            <p className={`text-[10px] ${days <= 0 ? "text-red-500" : days <= 3 ? "text-amber-500" : "text-gray-400"}`}>
                              {days <= 0 ? "Vencido" : `En ${days} días`}
                            </p>
                          )}
                        </div>
                      ) : <span className="text-gray-300 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${st.cls}`}>
                        <Icon size={11} />
                        {st.label}
                      </span>
                    </td>
                    <td className="px-3 py-4">
                      <div className="flex items-center gap-1">
                        <ChevronRight size={14} className="text-gray-300" />
                        <button onClick={(e) => { e.stopPropagation(); setLinkingOffer(o); }}
                          className={`p-1 transition-colors ${o.linked_licitacion_id ? "text-blue-500 hover:text-blue-700" : "text-gray-300 hover:text-blue-600"}`}
                          title={o.linked_licitacion_id ? "Cambiar vinculación con Licitación" : "Vincular con Licitación"}
                          data-testid={`link-offer-${o.id}`}>
                          <Link2 size={13} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setLinkingTender360Offer(o); }}
                          className={`p-1 transition-colors ${o.linked_tender360_id ? "text-emerald-600 hover:text-emerald-800" : "text-gray-300 hover:text-emerald-600"}`}
                          title={o.linked_tender360_id ? "Cambiar vinculación con Tender360" : "Vincular con Tender360"}
                          data-testid={`link-tender360-offer-${o.id}`}>
                          <Target size={13} />
                        </button>
                        {o.status !== "desestimada" && o.status !== "approved" && (
                          <button onClick={(e) => { e.stopPropagation(); setDesestimando({ id: o.id, motivo: "" }); }}
                            className="text-gray-300 hover:text-slate-500 p-1 transition-colors" title="Desestimar" data-testid={`desestimar-offer-${o.id}`}>
                            <Ban size={13} />
                          </button>
                        )}
                        <button onClick={(e) => handleDelete(o.id, e)} className="text-gray-200 hover:text-red-500 p-1 transition-colors" data-testid={`delete-offer-${o.id}`}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl" data-testid="create-offer-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold font-['Outfit']">Nueva Oferta / Licitación</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><XCircle size={18} /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Nombre de la oferta *</label>
                <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  placeholder="EC Estacionamiento Regulado [Municipio]"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="offer-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Expediente / Licitación</label>
                  <input value={form.licitacion} onChange={e => setForm({...form, licitacion: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Tipo contrato</label>
                  <select value={form.contract_type} onChange={e => setForm({...form, contract_type: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="offer-contract-type-select">
                    {CONTRACT_TYPES.map(ct => <option key={ct.code} value={ct.code}>{ct.code} — {ct.label}</option>)}
                  </select>
                  {form.contract_type === "OTROS" && (
                    <input value={form.contract_type_other} onChange={e => setForm({...form, contract_type_other: e.target.value})}
                      placeholder="Describe el tipo (obligatorio si es Otros)"
                      className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#007934]"
                      data-testid="offer-contract-type-other-input" />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Municipio</label>
                  <input value={form.municipality} onChange={e => setForm({...form, municipality: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1 block">Deadline</label>
                  <input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Responsable negocio</label>
                <input value={form.responsible_business} onChange={e => setForm({...form, responsible_business: e.target.value})}
                  placeholder="Nombre y apellidos"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Email negocio (para notificaciones)</label>
                <input type="email" value={form.responsible_business_email} onChange={e => setForm({...form, responsible_business_email: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">Descripción</label>
                <textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" />
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
                <button type="submit" disabled={creating} className="flex-1 bg-[#007934] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#006528] disabled:opacity-60" data-testid="create-offer-submit">
                  {creating ? "Creando..." : "Crear y abrir"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* AI Wizard for new TIC offer from Excel */}
      <OfferWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onCreated={() => load()}
      />
      <ImportOfferJsonModal
        open={showImportJson}
        onClose={() => setShowImportJson(false)}
        onImported={() => load()}
      />
      <LinkLicitacionModal
        open={!!linkingOffer}
        offer={linkingOffer}
        onClose={() => setLinkingOffer(null)}
        onLinked={() => { load(); loadKpis(); loadMatchHistory(); }}
      />
      <LinkTender360Modal
        open={!!linkingTender360Offer}
        offer={linkingTender360Offer}
        onClose={() => setLinkingTender360Offer(null)}
        onLinked={() => { load(); loadKpis(); }}
      />
    </div>
  );
}
